import type { ClaimVariant } from "~/hec/loyalty/types";
import { bySiteId } from "~/hec/utils/site-id";

import type { InsidersClaimCopy, ResolvedInsidersClaimCopy } from "./types";

/**
 * Default card copy, taken from the live FSA Insiders page (2026-10-05); the
 * HSA page swaps the plan code. In SFRA the card copy is Business Manager
 * content — only the control labels live in code (`loyalty.properties`,
 * `common.properties`), and those are mirrored below.
 */
type PlanCode = "FSA" | "HSA";

// WDH has no Insiders program; its entry is only a fallback for a misplaced
// block, and `bySiteId` keeps the three-site coverage explicit.
const planCodeForSite = (siteId: string): PlanCode =>
  bySiteId<PlanCode>(siteId, { FSASTORE: "FSA", HSASTORE: "HSA", WDH: "FSA" });

const PERKS = "Perks<sup>®</sup>";

export const DEFAULT_HEADING = "30 Points";
/** `common.properties` → `button.claim`. */
export const DEFAULT_BUTTON_LABEL = "Claim";
/** `loyalty.properties` → `futurePlans.yes.label` / `futurePlans.no.label`. */
export const DEFAULT_YES_LABEL = "Yes, I plan to enroll";
export const DEFAULT_NO_LABEL = "No, I don't plan to enroll";

/** `loyalty.properties` → `annualAmount.placeholder` (FSA override in `app_fsa`). */
export const defaultInputLabel = (siteId: string): string =>
  `${planCodeForSite(siteId)} Contribution`;

const VARIANT_DEFAULTS: Record<
  ClaimVariant,
  (plan: PlanCode) => { eyebrow: string; body: string }
> = {
  monthly: () => ({
    eyebrow: "1X PER MONTH",
    body: `Earn 30 ${PERKS} Points when you visit this page once a month.`,
  }),
  annualContribution: plan => ({
    eyebrow: "1X PER YEAR",
    body: `Earn 30 ${PERKS} Points when you submit your ${plan} contribution amount once a year.`,
  }),
  futurePlans: plan => ({
    eyebrow: "1X PER YEAR",
    body: `Earn 30 ${PERKS} Points when you let us know if you plan to keep your ${plan} plan.`,
  }),
};

/** Editor-only sample replies (`loyalty.properties` → `message.success`; a typical Annex rejection). */
export const EDITOR_SAMPLE_SUCCESS =
  "Congratulations! You just earned 30 Perks points!";
export const EDITOR_SAMPLE_ERROR =
  "You have already claimed this credit. Please try again next month.";

const pick = (value: string | undefined, fallback: string): string =>
  value !== undefined && value.trim() !== "" ? value : fallback;

export function resolveInsidersClaimCopy(
  copy: InsidersClaimCopy | undefined,
  variant: ClaimVariant,
  siteId: string,
): ResolvedInsidersClaimCopy {
  const defaults = VARIANT_DEFAULTS[variant](planCodeForSite(siteId));
  return {
    eyebrow: pick(copy?.eyebrow, defaults.eyebrow),
    heading: pick(copy?.heading, DEFAULT_HEADING),
    body: pick(copy?.body, defaults.body),
    buttonLabel: pick(copy?.buttonLabel, DEFAULT_BUTTON_LABEL),
    inputLabel: pick(copy?.inputLabel, defaultInputLabel(siteId)),
    yesLabel: pick(copy?.yesLabel, DEFAULT_YES_LABEL),
    noLabel: pick(copy?.noLabel, DEFAULT_NO_LABEL),
  };
}
